import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLocalStorage } from 'usehooks-ts';
import { useAddAudioRecordingDevice, useValidateDeviceSerial, useUpdateDeviceMetadata } from 'web/services/api/queries';
import { collectDeviceMetadata } from 'web/utils/DeviceMetadataUtil';

export default function useEnsureDevice() {
    const [deviceSerial, setDeviceSerial] = useLocalStorage<string>('deviceSerial', '');
    const [deviceId, setDeviceId] = useLocalStorage<string | null>('deviceId', null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { mutate: addAudioRecordingDevice, isLoading: isAdding } = useAddAudioRecordingDevice();
    const { mutate: updateDeviceMetadata } = useUpdateDeviceMetadata();
    const { data: isValid, refetch: _, isFetching: isValidating } = useValidateDeviceSerial(deviceId ?? '', deviceSerial);

    const initializeDevice = useCallback((serial: string) => {
        setIsLoading(true);
        addAudioRecordingDevice(serial, {
            onSuccess: (newDeviceId) => {
                setDeviceSerial(serial);
                setDeviceId(newDeviceId);
                setIsLoading(false);
            },
            onError: (error) => {
                if (error === "Device serial already exist") {
                    setDeviceSerial(serial);
                } else {
                    console.error("Error adding new device:", error);
                    setError("Error ensuring device exists");
                }
                setIsLoading(false);
            }
        });
    }, [addAudioRecordingDevice]);

    useEffect(() => {
        if (isValidating) {
            return;
        }

        if (!deviceSerial) {
            const newSerial = generateNewSerial();
            initializeDevice(newSerial);
        } else {
            if (!isValid) {
                initializeDevice(deviceSerial);
            } else {
                updateDeviceMetadata({ deviceSerial, metadata: collectDeviceMetadata(undefined, undefined, false) });
            }
        }
    }, [isValidating, deviceSerial, isValid, initializeDevice]);

    function generateNewSerial() {
        return "WEB-" + uuidv4();
    }

    return { deviceSerial, isLoading: isLoading || isAdding || isValidating, error };
}
